// Armazenamento de rascunhos resiliente (iOS/Android): IndexedDB + fallback localStorage
export type DraftAny = any;
const DB_NAME = 'acor-drafts';
const STORE = 'drafts';
let dbPromise: Promise<IDBDatabase> | null = null;

function openDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    try {
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    } catch (e) { reject(e); }
  });
  return dbPromise;
}

async function idbGet(key: string) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const st = tx.objectStore(STORE);
    const req = st.get(key);
    req.onsuccess = () => resolve(req.result ?? null);
    req.onerror = () => reject(req.error);
  });
}
async function idbSet(key: string, value: DraftAny) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    const st = tx.objectStore(STORE);
    const req = st.put(value, key);
    req.onsuccess = () => resolve(undefined);
    req.onerror = () => reject(req.error);
  });
}
async function idbDel(key: string) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    const st = tx.objectStore(STORE);
    const req = st.delete(key);
    req.onsuccess = () => resolve(undefined);
    req.onerror = () => reject(req.error);
  });
}

function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && (navigator as any).maxTouchPoints > 1);
}

export const draftStore = {
  isIOS,
  async get<T = DraftAny>(key: string): Promise<T | null> {
    try {
      const fromIDB = (await idbGet(key)) as T | null;
      if (fromIDB != null) return fromIDB;
      
      // Migração automática: promover localStorage -> IndexedDB (compatibilidade com versões antigas / Android)
      const raw = localStorage.getItem(key);
      if (!raw) {
        // Se não há no localStorage, tentar sessionStorage como último recurso
        try {
          const sessionRaw = sessionStorage.getItem(key);
          return sessionRaw ? JSON.parse(sessionRaw) : null;
        } catch {
          return null;
        }
      }
      
      const obj = JSON.parse(raw) as T;
      // Migrar para IndexedDB e limpar localStorage (melhora performance futura)
      try { 
        await idbSet(key, obj); 
        localStorage.removeItem(key);
        console.log(`📦 Draft migrado para IndexedDB: ${key}`);
      } catch {}
      return obj;
    } catch {
      // Fallback para localStorage se IndexedDB falhar completamente
      try { 
        const raw = localStorage.getItem(key); 
        return raw ? JSON.parse(raw) : null; 
      }
      catch { 
        // Último recurso: sessionStorage
        try {
          const sessionRaw = sessionStorage.getItem(key);
          return sessionRaw ? JSON.parse(sessionRaw) : null;
        } catch {
          return null;
        }
      }
    }
  },
  
  async set(key: string, value: DraftAny) {
    const serialized = JSON.stringify(value);
    const promises: Promise<void>[] = [];
    
    // Salvar em múltiplas camadas simultaneamente
    promises.push(
      // SessionStorage (imediato, para iOS)
      new Promise<void>((resolve) => {
        try { sessionStorage.setItem(key, serialized); }
        catch (e) { console.warn('Draft: SessionStorage failed:', e); }
        resolve();
      }),
      
      // LocalStorage (confiável)
      new Promise<void>((resolve) => {
        try { localStorage.setItem(key, serialized); }
        catch (e) { console.warn('Draft: LocalStorage failed:', e); }
        resolve();
      })
    );
    
    // IndexedDB (melhor para Android)
    if (!isIOS() || window.navigator.userAgent.includes('Chrome')) {
      promises.push(
        idbSet(key, value).catch(e => {
          console.warn('Draft: IndexedDB failed:', e);
        }).then(() => {})
      );
    }
    
    await Promise.allSettled(promises);
  },
  
  async remove(key: string) {
    const promises: Promise<void>[] = [
      new Promise<void>((resolve) => {
        try { sessionStorage.removeItem(key); } catch {}
        resolve();
      }),
      new Promise<void>((resolve) => {
        try { localStorage.removeItem(key); } catch {}
        resolve();
      }),
      idbDel(key).catch(() => {}).then(() => {})
    ];
    
    await Promise.allSettled(promises);
  },
};
